import { showAlert } from "@/components/global-alert-dialog";
import { captureException, withScope } from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";

/**
 * The request never got an HTTP response: fetch itself rejected (offline, a
 * sleeping laptop, a dropped connection). Says nothing about the server.
 */
const isNetworkFailure = (err: Error): boolean =>
  err instanceof TRPCClientError &&
  err.cause instanceof TypeError &&
  err.meta?.response === undefined;

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          variables: mutation.state.variables,
        });
        if (mutation.options.mutationKey) {
          scope.setFingerprint(
            // Duplicate to prevent modification
            Array.from(mutation.options.mutationKey) as string[],
          );
        }
        captureException(err);
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      // Live pages refetch many queries at once when the connection comes
      // back; reporting each one buries real errors.
      if (isNetworkFailure(err)) return;
      withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash });
        scope.setFingerprint([query.queryHash.replaceAll(/[0-9]/g, "0")]);
        captureException(err);
      });
    },
  }),
  defaultOptions: {
    mutations: {
      onError: (err) => {
        showAlert({
          title: "Error",
          description: err.message ?? "Unknown error",
          actions: [{ label: "Dismiss", value: null, variant: "outline" }],
        });
      },
    },
  },
});
